// UltraFlex Admin Portal - Member Management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import { 
  Search, Filter, MoreVertical, ChevronLeft, ChevronRight,
  User, Mail, Phone, MapPin, CreditCard, Calendar, AlertCircle,
  CheckCircle, XCircle, Clock, Pause, UserX, UserCheck, FileText,
  Shield, Download, Edit2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMembers = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showAgreement, setShowAgreement] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchMembers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, statusFilter, siteFilter, page]);

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSites(data.items || []);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });
      
      if (searchQuery) params.append('query', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      if (siteFilter) params.append('site_id', siteFilter);

      const res = await fetch(`${API_URL}/api/members?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      setMembers(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewMember = async (memberId) => {
    try {
      const res = await fetch(`${API_URL}/api/members/${memberId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSelectedMember(data);
      setShowMemberModal(true);
    } catch (error) {
      console.error('Failed to fetch member:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'green', icon: CheckCircle, label: 'Active' },
      paid: { color: 'green', icon: CheckCircle, label: 'Paid' },
      pending: { color: 'yellow', icon: Clock, label: 'Pending' },
      grace: { color: 'orange', icon: AlertCircle, label: 'Grace' },
      failed: { color: 'red', icon: XCircle, label: 'Failed' },
      suspended: { color: 'red', icon: UserX, label: 'Suspended' },
      cancelled: { color: 'gray', icon: XCircle, label: 'Cancelled' },
      frozen: { color: 'blue', icon: Pause, label: 'Frozen' }
    };

    const config = statusConfig[status] || { color: 'gray', icon: AlertCircle, label: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-500/20 text-${config.color}-400`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-uf-black">
      {/* If member selected → show full-page card (drill-to-card pattern) */}
      {showMemberModal && selectedMember ? (
        <MemberDetailModal
          member={selectedMember}
          onClose={() => { setShowMemberModal(false); setSelectedMember(null); }}
          token={token}
          onUpdate={fetchMembers}
        />
      ) : (<>
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-4 md:px-8 py-4 md:py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-uf-text">Member Management</h1>
            <p className="text-uf-text-muted mt-1">{total.toLocaleString()} total members</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 md:px-8 py-4 bg-uf-surface-hi border-b border-uf-border">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-uf-text-muted pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or member number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="admin-members-search"
              className="w-full pl-12 pr-4 py-2.5 bg-uf-surface border border-uf-border rounded-lg text-sm text-uf-text placeholder-uf-text-muted focus:ring-2 focus:ring-uf-red focus:outline-none"
             
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paid">Paid</option>
            <option value="grace">Grace Period</option>
            <option value="failed">Failed</option>
            <option value="suspended">Suspended</option>
            <option value="cancelled">Cancelled</option>
            <option value="frozen">Frozen</option>
          </select>

          {/* Site Filter */}
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
          >
            <option value="">All Sites</option>
            {sites.map(site => (
              <option key={site.id} value={site.id}>{site.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Members Table */}
      <div className="p-4 md:p-8">
        <div className="bg-uf-surface rounded-lg border border-uf-border overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
            </div>
          ) : members.length === 0 ? (
            <div className="p-12 text-center text-uf-text-muted">
              <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No members found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-uf-surface-hi">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Plan</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-uf-text-muted uppercase">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-uf-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-uf-border">
                {members.map((member) => (
                  <tr key={member.id} data-testid={`member-row-${member.id}`} onClick={() => viewMember(member.id)} className="hover:bg-uf-surface-hi transition-colors cursor-pointer">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-uf-red/20 flex items-center justify-center mr-3">
                          <span className="text-uf-red font-semibold">
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-uf-text font-medium">{member.first_name} {member.last_name}</p>
                          <p className="text-uf-text-muted text-xs font-mono">{member.member_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        <span className="text-uf-text text-sm flex items-center">
                          <Mail className="w-3 h-3 mr-2 text-uf-text-muted" />
                          {member.email}
                        </span>
                        <span className="text-uf-text-muted text-sm flex items-center">
                          <Phone className="w-3 h-3 mr-2" />
                          {member.phone}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-uf-text">{member.plan_name || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(member.subscription_status)}
                    </td>
                    <td className="px-6 py-4 text-uf-text-muted text-sm">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <ChevronRight className="w-4 h-4 text-uf-text-muted inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-uf-surface-hi border-t border-uf-border flex items-center justify-between">
              <span className="text-uf-text-muted text-sm">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50 hover:bg-uf-surface-hi transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded bg-uf-surface border border-uf-border text-uf-text disabled:opacity-50 hover:bg-uf-surface-hi transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Member Detail Modal */}
      </>)}
    </div>
  );
};

const MemberDetailModal = ({ member, onClose, token, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [agreement, setAgreement] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchNotes(); }, [member.id]);

  const fetchAgreement = async () => {
    setAgreementLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agreements/member/${member.id}`, { headers });
      if (res.ok) { const data = await res.json(); setAgreement(data[0] || null); }
      else { setAgreement(null); }
    } catch { setAgreement(null); }
    setAgreementLoading(false);
  };

  const fetchAccessLogs = async () => {
    setAccessLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/access/logs?member_id=${member.id}&limit=20`, { headers });
      if (res.ok) { const data = await res.json(); setAccessLogs(data.items || data || []); }
    } catch { setAccessLogs([]); }
    setAccessLoading(false);
  };

  const startEditing = () => {
    setEditData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      phone: member.phone || '',
      email: member.email || '',
      date_of_birth: member.date_of_birth || '',
      address_line1: member.address_line1 || '',
      city: member.city || '',
      postcode: member.postcode || '',
      emergency_contact_name: member.emergency_contact_name || '',
      emergency_contact_phone: member.emergency_contact_phone || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/members/${member.id}`, {
        method: 'PUT', headers, body: JSON.stringify(editData)
      });
      if (res.ok) { setEditing(false); onUpdate(); onClose(); }
      else { const err = await res.json().catch(() => ({})); alert(err.detail || 'Failed to update member'); }
    } catch { alert('Failed to save changes'); }
    setEditSaving(false);
  };

  const fetchNotes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/notes/member/${member.id}`, { headers });
      if (res.ok) setNotes(await res.json());
    } catch { setNotes([]); }
  };

  const addNote = async () => {
    if (!noteContent.trim()) return;
    await fetch(`${API_URL}/api/crm/notes/member/${member.id}`, {
      method: 'POST', headers, body: JSON.stringify({ content: noteContent, note_type: noteType })
    });
    setNoteContent(''); setNoteType('general'); setShowAddNote(false); fetchNotes();
  };

  const deleteNote = async (noteId) => {
    await fetch(`${API_URL}/api/crm/notes/${noteId}`, { method: 'DELETE', headers });
    fetchNotes();
  };

  const togglePin = async (noteId, pinned) => {
    await fetch(`${API_URL}/api/crm/notes/${noteId}`, { method: 'PATCH', headers, body: JSON.stringify({ is_pinned: !pinned }) });
    fetchNotes();
  };

  const handleSuspend = async () => {
    const reason = prompt('Enter reason for suspension:');
    if (!reason) return;
    setActionLoading(true);
    try {
      await fetch(`${API_URL}/api/members/${member.id}/suspend?reason=${encodeURIComponent(reason)}`, { method: 'POST', headers });
      onUpdate(); onClose();
    } catch { alert('Failed'); }
    setActionLoading(false);
  };

  const handleReinstate = async () => {
    const reason = prompt('Enter reason for reinstatement:');
    if (!reason) return;
    setActionLoading(true);
    try {
      await fetch(`${API_URL}/api/members/${member.id}/reinstate?reason=${encodeURIComponent(reason)}`, { method: 'POST', headers });
      onUpdate(); onClose();
    } catch { alert('Failed'); }
    setActionLoading(false);
  };

  const noteTypeColors = { general: 'bg-gray-500/20 text-gray-400', call: 'bg-blue-500/20 text-blue-400', meeting: 'bg-violet-500/20 text-violet-400', follow_up: 'bg-amber-500/20 text-amber-400', complaint: 'bg-red-500/20 text-red-400', feedback: 'bg-emerald-500/20 text-emerald-400' };

  return (
    <div className="min-h-screen bg-uf-black" data-testid="member-card-page">
      {/* Back button */}
      <div className="px-4 md:px-8 py-4 border-b border-uf-border bg-uf-surface">
        <button onClick={onClose} data-testid="member-card-back" className="flex items-center gap-2 text-uf-text-muted hover:text-uf-text text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to Members list
        </button>
      </div>
      <div className="max-w-5xl mx-auto p-4 md:p-6">
      <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
        {/* Header - Contact Card */}
        <div className="p-5 border-b border-uf-border bg-gradient-to-r from-uf-surface to-uf-surface-hi">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 ring-2 ring-emerald-500/30 flex items-center justify-center">
                <span className="text-xl font-bold text-emerald-400">{member.first_name?.[0]}{member.last_name?.[0]}</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-uf-text" data-testid="member-card-name">{member.first_name} {member.last_name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/20 text-emerald-400">MEMBER</span>
                  <span className="text-uf-text-muted text-xs font-mono">{member.member_number}</span>
                  {getStatusBadgeSmall(member.subscription_status)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1.5 text-uf-text-muted"><Mail className="w-3.5 h-3.5 text-uf-red" />{member.email}</span>
            {member.phone && <span className="flex items-center gap-1.5 text-uf-text-muted"><Phone className="w-3.5 h-3.5 text-uf-red" />{member.phone}</span>}
            {member.home_site_name && <span className="flex items-center gap-1.5 text-uf-text-muted"><MapPin className="w-3.5 h-3.5 text-uf-red" />{member.home_site_name}</span>}
            {member.created_at && <span className="flex items-center gap-1.5 text-uf-text-muted"><Calendar className="w-3.5 h-3.5 text-uf-red" />Joined {new Date(member.created_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-uf-border px-2 overflow-x-auto">
          {[
            { id: 'profile', label: 'Details' },
            { id: 'subscription', label: 'Subscription' },
            { id: 'access', label: 'Access Log' },
            { id: 'notes', label: `Notes (${notes.length})` },
            { id: 'agreement', label: 'Agreement' },
          ].map(t => (
            <button key={t.id} onClick={() => { setActiveTab(t.id); if (t.id === 'agreement' && !agreement) fetchAgreement(); if (t.id === 'access' && accessLogs.length === 0) fetchAccessLogs(); }}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === t.id ? 'border-uf-red text-uf-red' : 'border-transparent text-uf-text-muted hover:text-uf-text'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[50vh]">
          {activeTab === 'profile' && (
            <div>
              <div className="flex justify-end mb-3">
                {!editing ? (
                  <PermissionGate perm="members.edit">
                    <button onClick={startEditing} className="flex items-center gap-1.5 bg-uf-red/10 text-uf-red px-3 py-1.5 rounded-lg text-xs hover:bg-uf-red/20" data-testid="edit-member-btn">
                      <Edit2 className="w-3 h-3" />Edit Member
                    </button>
                  </PermissionGate>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-uf-text-muted text-xs hover:text-uf-text">Cancel</button>
                    <button onClick={saveEdit} disabled={editSaving} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs" data-testid="save-member-edit-btn">{editSaving ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                )}
              </div>
              {editing ? (
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'first_name', label: 'First Name' },
                    { key: 'last_name', label: 'Last Name' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
                    { key: 'address_line1', label: 'Address' },
                    { key: 'city', label: 'City' },
                    { key: 'postcode', label: 'Postcode' },
                    { key: 'emergency_contact_name', label: 'Emergency Contact' },
                    { key: 'emergency_contact_phone', label: 'Emergency Phone' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-uf-text-muted text-xs block mb-1">{f.label}</label>
                      <input type={f.type || 'text'} value={editData[f.key] || ''} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid={`edit-member-${f.key}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <DetailRow icon={Mail} label="Email" value={member.email} />
                  <DetailRow icon={Phone} label="Phone" value={member.phone} />
                  <DetailRow icon={Calendar} label="Date of Birth" value={member.date_of_birth} />
                  <DetailRow icon={MapPin} label="Address" value={[member.address_line1, member.city, member.postcode].filter(Boolean).join(', ')} />
                  <DetailRow icon={User} label="Emergency Contact" value={member.emergency_contact_name ? `${member.emergency_contact_name} - ${member.emergency_contact_phone || ''}` : 'Not set'} />
                </div>
              )}
            </div>
          )}

          {activeTab === 'access' && (
            <div data-testid="member-access-log-tab">
              {accessLoading ? (
                <div className="text-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-uf-red mx-auto" /></div>
              ) : accessLogs.length > 0 ? (
                <div className="space-y-2">
                  {accessLogs.map((log, i) => (
                    <div key={log.id || i} className="flex items-center justify-between bg-uf-surface-hi rounded-lg p-3 border border-uf-border" data-testid={`access-log-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${log.action === 'entry' ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                          {log.action === 'entry' ? <UserCheck className="w-4 h-4 text-emerald-400" /> : <UserX className="w-4 h-4 text-amber-400" />}
                        </div>
                        <div>
                          <p className="text-uf-text text-sm capitalize">{log.action || 'Entry'}</p>
                          <p className="text-uf-text-muted text-xs">{log.site_name || 'Unknown Site'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-0.5 rounded ${log.status === 'granted' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{log.status}</span>
                        <p className="text-uf-text-muted text-xs mt-0.5">{log.timestamp ? new Date(log.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-uf-text-muted opacity-50" />
                  <p className="text-uf-text-muted text-sm">No access logs found</p>
                  <p className="text-uf-text-muted text-xs mt-1">Entry and exit records will appear here once the member uses their QR code.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DetailRow icon={CreditCard} label="Plan" value={member.plan_name || 'None'} />
                <DetailRow icon={AlertCircle} label="Status" value={member.subscription_status || 'Inactive'} />
                <DetailRow icon={Calendar} label="Next Payment" value={member.next_payment_date || '-'} />
                <DetailRow icon={CreditCard} label="Amount" value={member.next_payment_amount ? `£${member.next_payment_amount}` : '-'} />
                <DetailRow icon={CreditCard} label="Mandate" value={member.mandate_status || 'Not set up'} />
              </div>
              {member.entitlements?.length > 0 && (
                <div className="pt-3 border-t border-uf-border">
                  <p className="text-uf-text-muted text-xs mb-2 uppercase font-semibold tracking-wider">Entitlements</p>
                  <div className="flex flex-wrap gap-2">
                    {member.entitlements.map((s, i) => <span key={i} className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">{s}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div data-testid="member-notes-section">
              <div className="flex justify-between items-center mb-3">
                <p className="text-uf-text-muted text-xs">Internal notes about this member</p>
                <button data-testid="add-member-note-btn" onClick={() => setShowAddNote(true)}
                  className="flex items-center gap-1.5 bg-uf-red/10 text-uf-red px-3 py-1.5 rounded-lg text-xs hover:bg-uf-red/20">
                  <FileText className="w-3 h-3" />Add Note
                </button>
              </div>

              {showAddNote && (
                <div className="mb-3 bg-uf-surface-hi rounded-lg border border-uf-border p-3 space-y-2">
                  <select value={noteType} onChange={e => setNoteType(e.target.value)} className="bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-uf-text text-xs w-full">
                    {['general','call','meeting','follow_up','complaint','feedback'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                  </select>
                  <textarea data-testid="member-note-input" value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write a note..." rows={3}
                    className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => { setShowAddNote(false); setNoteContent(''); }} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                    <button data-testid="save-member-note-btn" onClick={addNote} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs">Save</button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {notes.map(n => (
                  <div key={n.id} className={`rounded-lg border p-3 ${n.is_pinned ? 'border-amber-500/30 bg-amber-500/5' : 'border-uf-border bg-uf-surface-hi'}`} data-testid={`member-note-${n.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 text-[10px]">
                          <span className={`px-1.5 py-0.5 rounded capitalize ${noteTypeColors[n.note_type] || noteTypeColors.general}`}>{n.note_type?.replace('_',' ')}</span>
                          {n.is_pinned && <span className="text-amber-400">pinned</span>}
                          <span className="text-uf-text-muted">{n.created_at?.replace('T',' ').slice(0,16)}</span>
                          {n.staff_name && <span className="text-uf-text-muted">by {n.staff_name}</span>}
                        </div>
                        <p className="text-uf-text text-sm whitespace-pre-wrap">{n.content}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => togglePin(n.id, n.is_pinned)} className="p-1 text-uf-text-muted hover:text-amber-400" title={n.is_pinned ? 'Unpin' : 'Pin'}>
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteNote(n.id)} className="p-1 text-uf-text-muted hover:text-red-400" title="Delete">
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {!notes.length && !showAddNote && <p className="text-uf-text-muted text-xs text-center py-4">No notes yet. Add one to get started.</p>}
              </div>
            </div>
          )}

          {/* Agreement Tab */}
          {activeTab === 'agreement' && (
            <div className="p-4 space-y-3" data-testid="member-agreement-tab">
              {agreementLoading ? (
                <div className="text-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-uf-red mx-auto" /></div>
              ) : agreement ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-400 text-sm font-medium">Agreement on file</span>
                    <span className="text-uf-text-muted text-xs ml-auto">{agreement.is_migration ? 'Migration' : 'New Signup'} &middot; v{agreement.agreement_version}</span>
                  </div>
                  <button onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/api/agreements/export/${agreement.id}`, { headers });
                      if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `Agreement_${member.member_number || member.first_name}.pdf`; a.click(); URL.revokeObjectURL(url); }
                    } catch {}
                  }} data-testid="download-agreement-pdf" className="flex items-center gap-2 w-full justify-center bg-uf-red/10 text-uf-red hover:bg-uf-red hover:text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors mb-4">
                    <Download className="w-4 h-4" />Download Agreement PDF
                  </button>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <span className="text-uf-text-muted">Signed by</span><span className="text-uf-text font-serif italic">{agreement.signature_full_name}</span>
                    <span className="text-uf-text-muted">Date</span><span className="text-uf-text">{agreement.signature_date}</span>
                    <span className="text-uf-text-muted">Completed</span><span className="text-uf-text">{agreement.completed_at ? new Date(agreement.completed_at).toLocaleString() : '-'}</span>
                    <span className="text-uf-text-muted">Declaration</span><span className={agreement.decl_accurate_health_info ? 'text-emerald-400' : 'text-red-400'}>{agreement.decl_accurate_health_info ? 'All confirmed' : 'Incomplete'}</span>
                    <span className="text-uf-text-muted">Health & Safety</span><span className={agreement.hs_attend_induction ? 'text-emerald-400' : 'text-red-400'}>{agreement.hs_attend_induction ? 'All confirmed' : 'Incomplete'}</span>
                    <span className="text-uf-text-muted">Ice Bath Waiver</span><span className={agreement.waiver_ice_sauna_accepted ? 'text-emerald-400' : 'text-red-400'}>{agreement.waiver_ice_sauna_accepted ? 'Accepted' : 'Not accepted'}</span>
                    <span className="text-uf-text-muted">T&Cs</span><span className={agreement.terms_accepted ? 'text-emerald-400' : 'text-red-400'}>{agreement.terms_accepted ? `Accepted (${agreement.terms_version})` : 'Not accepted'}</span>
                    <span className="text-uf-text-muted">Marketing</span><span className="text-uf-text">{agreement.marketing_opt_in ? 'Opted in' : 'Opted out'}</span>
                  </div>
                  {/* PAR-Q answers */}
                  <div className="mt-3 pt-3 border-t border-uf-border">
                    <p className="text-uf-text-muted text-[10px] uppercase tracking-wider mb-2">PAR-Q Health Answers</p>
                    <div className="grid grid-cols-1 gap-1 text-xs">
                      {[
                        ['Supervised activity advised', agreement.parq_supervised_activity],
                        ['Chest pain', agreement.parq_chest_pain],
                        ['Dizziness/loss of balance', agreement.parq_dizziness],
                        ['Bone/joint issues', agreement.parq_bone_joint_issues],
                        ['Pregnant/postpartum', agreement.parq_pregnant_postpartum],
                        ['Recent surgery', agreement.parq_recent_surgery],
                        ['Chronic illness', agreement.parq_chronic_illness],
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between py-0.5">
                          <span className="text-uf-text-muted">{label}</span>
                          <span className={val ? 'text-amber-400' : 'text-uf-text'}>{val ? 'YES' : 'NO'}</span>
                        </div>
                      ))}
                      {agreement.parq_chronic_details && <div className="bg-zinc-900 rounded p-2 mt-1"><p className="text-uf-text-muted text-[10px]">Chronic details:</p><p className="text-uf-text text-xs">{agreement.parq_chronic_details}</p></div>}
                      {agreement.parq_doctor_name && <div className="flex justify-between py-0.5"><span className="text-uf-text-muted">Doctor</span><span className="text-uf-text">{agreement.parq_doctor_name} {agreement.parq_doctor_phone ? `(${agreement.parq_doctor_phone})` : ''}</span></div>}
                    </div>
                  </div>
                  {/* Device forensics */}
                  <div className="mt-3 pt-3 border-t border-uf-border">
                    <p className="text-uf-text-muted text-[10px] uppercase tracking-wider mb-2">Device & Session Evidence</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <span className="text-uf-text-muted">IP Address</span><span className="text-uf-text font-mono">{agreement.ip_address || '-'}</span>
                      <span className="text-uf-text-muted">Browser</span><span className="text-uf-text">{agreement.browser_name} {agreement.browser_version}</span>
                      <span className="text-uf-text-muted">OS</span><span className="text-uf-text">{agreement.os_name} {agreement.os_version}</span>
                      <span className="text-uf-text-muted">Device</span><span className="text-uf-text capitalize">{agreement.device_type || '-'}</span>
                      <span className="text-uf-text-muted">Screen</span><span className="text-uf-text">{agreement.screen_resolution || '-'}</span>
                      <span className="text-uf-text-muted">Timezone</span><span className="text-uf-text">{agreement.timezone || '-'}</span>
                      <span className="text-uf-text-muted">Language</span><span className="text-uf-text">{agreement.language || '-'}</span>
                      <span className="text-uf-text-muted">Platform</span><span className="text-uf-text">{agreement.platform || '-'}</span>
                    </div>
                    {agreement.user_agent && <div className="mt-2 bg-zinc-900 rounded p-2"><p className="text-uf-text-muted text-[10px] mb-1">Full User Agent:</p><p className="text-uf-text text-[10px] font-mono break-all">{agreement.user_agent}</p></div>}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Shield className="w-8 h-8 mx-auto mb-2 text-amber-400 opacity-50" />
                  <p className="text-amber-400 text-sm">No agreement on file</p>
                  <p className="text-uf-text-muted text-xs mt-1">This member has not yet completed the digital agreement form.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-3 border-t border-uf-border flex justify-end space-x-3">
          {(member.subscription_status === 'suspended' || member.status === 'suspended') ? (
            <PermissionGate perm="members.create">
              <button onClick={handleReinstate} disabled={actionLoading} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 flex items-center" data-testid="reinstate-member-btn">
                <UserCheck className="w-4 h-4 mr-2" />Reinstate
              </button>
            </PermissionGate>
          ) : (
            <PermissionGate perm="members.create">
              <button onClick={handleSuspend} disabled={actionLoading} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center" data-testid="suspend-member-btn">
                <UserX className="w-4 h-4 mr-2" />Suspend
              </button>
            </PermissionGate>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

const getStatusBadgeSmall = (status) => {
  const c = { active: 'bg-emerald-500/20 text-emerald-400', paid: 'bg-emerald-500/20 text-emerald-400', pending: 'bg-amber-500/20 text-amber-400', suspended: 'bg-red-500/20 text-red-400', cancelled: 'bg-gray-500/20 text-gray-400', frozen: 'bg-cyan-500/20 text-cyan-400', failed: 'bg-red-500/20 text-red-400' };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] capitalize ${c[status] || 'bg-gray-500/20 text-gray-400'}`}>{status || 'unknown'}</span>;
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start">
    <Icon className="w-5 h-5 text-uf-text-muted mr-3 mt-0.5" />
    <div>
      <p className="text-uf-text-muted text-sm">{label}</p>
      <p className="text-uf-text">{value}</p>
    </div>
  </div>
);

export default AdminMembers;
